import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  Select,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import { PageContainer } from '@/layouts/PageContainer';
import { dayLabel, whenLabel } from '@/pages/calendar/time';
import { CertificateDrawer } from './CertificateDrawer';
import styles from './parity.module.css';

export interface Certificate {
  id: string;
  number: string;
  patient: { id: string; fullName: string };
  branch: { id: string; name: string };
  from: string | null;
  to: string | null;
  days: number | null;
  doctor: string | null;
  issuedAt: string;
  scans: number;
  revokedAt: string | null;
  revokeReason: string | null;
  overlapsWith: string[];
}
export interface CertificateList {
  rows: Certificate[];
  summary: { valid: number; revoked: number };
  signals: {
    overlaps: {
      number: string;
      patient: Certificate['patient'];
      overlapsWith: string[];
    }[];
    frequent: {
      patient: Certificate['patient'];
      count: number;
      days: number;
      doctors: string[];
    }[];
  };
}
export function MedicalCertificatesPage() {
  const { t } = useTranslation();
  const { selected } = useBranch();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  // Search goes to the API (number or patient name) after a short pause, so
  // older MCs beyond the latest 300 can be found; the returned rows are also
  // filtered as you type.
  const [term, setTerm] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setTerm(search.trim()), 350);
    return () => clearTimeout(h);
  }, [search]);
  const q = useQuery<CertificateList>({
    queryKey: ['medical-certificates', selected?.id ?? 'all', term, status],
    // Keep rows while a new search loads, never across a branch change.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === (selected?.id ?? 'all') ? prev : undefined,
    queryFn: ({ signal }) =>
      api
        .get<CertificateList>('/medical-certificates', {
          signal,
          query: {
            ...(term ? { q: term } : {}),
            ...(status !== 'all' ? { status } : {}),
          },
        })
        .then((r) => r.data),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  // A branch change drops the open MC: it is looked up in this branch's rows.
  const open = q.data?.rows.find((r) => r.id === openId);
  const period = (r: Certificate) =>
    [
      r.from ? dayLabel(r.from) : '—',
      r.to && r.to !== r.from ? dayLabel(r.to) : null,
    ]
      .filter(Boolean)
      .join(' – ');
  const state = (r: Certificate) => (
    <>
      <Badge tone={r.revokedAt ? 'danger' : 'success'}>
        {t(r.revokedAt ? 'certificates.revoked' : 'certificates.valid')}
      </Badge>
      {!r.revokedAt && !!r.overlapsWith.length && (
        <Badge tone="warning">{t('certificates.overlaps')}</Badge>
      )}
    </>
  );
  const rows = q.data?.rows.filter(
    (r) =>
      (status === 'all' || (status === 'revoked') === !!r.revokedAt) &&
      [r.number, r.patient.fullName, r.doctor ?? '', r.branch.name]
        .join(' ')
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
  );
  return (
    <PageContainer>
      <p className={styles.infoNotice}>{t('certificates.notice')}</p>
      <Card
        title={t('certificates.title')}
        flush
        actions={
          <>
            <Input
              className={styles.headerControl}
              aria-label={t('certificates.search')}
              placeholder={t('certificates.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              className={styles.headerControl}
              aria-label={t('operations.status')}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={['all', 'valid', 'revoked'].map((value) => ({
                value,
                label: t(`certificates.${value}`),
              }))}
            />
            {q.data && (
              <span className={styles.crumb}>
                {t('certificates.summary', q.data.summary)}
              </span>
            )}
          </>
        }
      >
        <DataTable
          caption={t('certificates.title')}
          rows={rows}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          empty={<EmptyState title={t('certificates.empty')} />}
          columns={[
            {
              key: 'serial',
              header: t('certificates.serial'),
              render: (r) => <span className={styles.mono}>{r.number}</span>,
            },
            {
              key: 'patient',
              header: t('operations.patient'),
              render: (r) => <strong>{r.patient.fullName}</strong>,
            },
            {
              key: 'period',
              header: t('certificates.period'),
              render: (r) => (
                <>
                  {period(r)}
                  <span className={styles.sub}>
                    {r.days === null
                      ? '—'
                      : t('certificates.days', { count: r.days })}
                  </span>
                </>
              ),
            },
            {
              key: 'doctor',
              header: t('books.doctor'),
              render: (r) => r.doctor ?? '—',
            },
            {
              key: 'issued',
              header: t('certificates.issued'),
              render: (r) => (
                <>
                  {whenLabel(r.issuedAt)}
                  <span className={styles.sub}>{r.branch.name}</span>
                </>
              ),
            },
            {
              key: 'scans',
              header: t('certificates.scans'),
              align: 'right',
              render: (r) => r.scans,
            },
            { key: 'status', header: t('operations.status'), render: state },
            {
              key: 'view',
              header: '',
              render: (r) => (
                <Button
                  size="sm"
                  onClick={() => setOpenId(r.id)}
                  aria-label={t('certificates.viewLabel', { number: r.number })}
                >
                  {t('certificates.view')}
                </Button>
              ),
            },
          ]}
        />
        <p className={styles.cardFoot}>{t('certificates.window')}</p>
      </Card>
      {!!q.data &&
        (!!q.data.signals.overlaps.length ||
          !!q.data.signals.frequent.length) && (
          <Card
            title={t('certificates.signals')}
            actions={
              <span className={styles.sub}>{t('certificates.reviewOnly')}</span>
            }
          >
            {q.data.signals.overlaps.map((r) => (
              <div className={styles.split} key={r.number}>
                <div>
                  <strong>
                    {t('certificates.overlapPatient', {
                      name: r.patient.fullName,
                    })}
                  </strong>
                  <span className={styles.sub}>
                    {r.number} · {r.overlapsWith.join(', ')}
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={() =>
                    setOpenId(
                      q.data!.rows.find((x) => x.number === r.number)?.id ??
                        null,
                    )
                  }
                >
                  {t('certificates.view')}
                </Button>
              </div>
            ))}
            {q.data.signals.frequent.map((r) => (
              <div key={r.patient.id}>
                <strong>
                  {t('certificates.frequent', {
                    count: r.count,
                    name: r.patient.fullName,
                  })}
                </strong>
                <span className={styles.sub}>
                  {t('certificates.days', { count: r.days })} ·{' '}
                  {r.doctors.join(', ')}
                </span>
              </div>
            ))}
          </Card>
        )}
      {open && (
        <CertificateDrawer
          id={open.id}
          overlapsWith={open.overlapsWith}
          onClose={() => setOpenId(null)}
        />
      )}
    </PageContainer>
  );
}
